/**
 * A dashboard tile that breaks a figure down by store.
 *
 * The tile it replaces showed a single number over a one-line verdict, and with
 * the allowance now per store that number had nothing honest to be. It ended up
 * showing the *store count* under a label reading "article limit", which is two
 * unrelated facts in one tile - a clinic with two stores of ten articles each saw
 * a large "2" and no way to find out that ten was the number that mattered.
 *
 * So the figure is per store, named, with a bar. Two stores fit; beyond that the
 * tile shows the two that need attention most and a control that opens the rest,
 * because a tile that grows with the clinic stops being a tile.
 *
 * The overflow uses a native `<dialog>`: `showModal()` brings the focus trap, the
 * backdrop, Escape to close and the inert background with it, and every one of
 * those is a thing a hand-rolled popup gets wrong.
 */
import { useRef } from 'react';
import { jaNumber } from './ui.tsx';

export interface StoreMetric {
  storeId: string;
  name: string;
  /** What this store has used, or produced. */
  value: number;
  /**
   * What it is measured against. null is unmetered, and the bar then shows the
   * store's share of the clinic total instead - which is the only ratio a
   * limitless count has.
   */
  limit: number | null;
}

/** Ratio, and what it means, in one place so the bar and the text cannot disagree. */
function ratioOf(metric: StoreMetric, shareBase: number): number {
  if (metric.limit !== null && metric.limit > 0) return Math.min(metric.value / metric.limit, 1);
  if (shareBase > 0) return Math.min(metric.value / shareBase, 1);
  return 0;
}

function toneOf(metric: StoreMetric): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (metric.limit === null || metric.limit <= 0) return 'neutral';
  const ratio = metric.value / metric.limit;
  return ratio >= 1 ? 'danger' : ratio >= 0.8 ? 'warn' : 'ok';
}

function StoreLine({
  metric,
  shareBase,
  unit,
}: {
  metric: StoreMetric;
  shareBase: number;
  unit: string;
}) {
  const tone = toneOf(metric);
  const ratio = ratioOf(metric, shareBase);
  const text =
    metric.limit === null
      ? `${jaNumber(metric.value)} ${unit}`
      : `${jaNumber(metric.value)} / ${jaNumber(metric.limit)}`;

  return (
    <div className="storeline">
      <span className="storeline__name" title={metric.name}>
        {metric.name}
      </span>
      <span className={`storeline__value storeline__value--${tone}`}>{text}</span>
      {/*
        role=meter with the real bounds, so the bar is not the only way to read
        this: a screen reader gets the same figures the sighted reader does, and
        the colour is never the only carrier of "this one is full".
      */}
      <div
        className={`storebar storebar--${tone}`}
        role="meter"
        aria-valuenow={metric.value}
        aria-valuemin={0}
        {...(metric.limit === null ? {} : { 'aria-valuemax': metric.limit })}
        aria-label={`${metric.name}：${text}`}
      >
        <div className="storebar__fill" style={{ inlineSize: `${(ratio * 100).toFixed(1)}%` }} />
      </div>
    </div>
  );
}

export function StoreTile({
  label,
  /** The clinic-level figure. Shown small, because the per-store rows are the point. */
  total,
  unit,
  metrics,
  /** How many rows fit before the rest move into the dialog. */
  inline = 2,
}: {
  label: string;
  total: string;
  unit: string;
  metrics: StoreMetric[];
  inline?: number;
}) {
  const dialog = useRef<HTMLDialogElement>(null);

  /*
   * The rows shown inline are the ones closest to their limit, not the first
   * two alphabetically. A tile with room for two rows should spend them on the
   * stores somebody needs to do something about.
   */
  const shareBase = metrics.reduce((sum, metric) => sum + metric.value, 0);
  const ranked = [...metrics].sort((a, b) => ratioOf(b, shareBase) - ratioOf(a, shareBase));
  const shown = ranked.slice(0, inline);
  const hidden = ranked.length - shown.length;

  return (
    <div className="stat storetile">
      <div className="stat__label">{label}</div>
      <div className="storetile__total">
        {total} <span className="storetile__unit">{unit}</span>
      </div>

      <div className="storetile__lines">
        {shown.map((metric) => (
          <StoreLine key={metric.storeId} metric={metric} shareBase={shareBase} unit={unit} />
        ))}
      </div>

      {hidden > 0 && (
        <>
          <button
            className="storetile__more"
            type="button"
            onClick={() => dialog.current?.showModal()}
          >
            ほか{jaNumber(hidden)}店舗を見る
          </button>

          {/*
            Closed by the form method rather than a click handler: `method="dialog"`
            is how a dialog closes itself, and it keeps working when the button is
            reached by keyboard.
          */}
          <dialog className="storedialog" ref={dialog}>
            <form method="dialog" className="storedialog__head">
              <h2>{label}</h2>
              <button className="btn btn--sm btn--ghost" type="submit">
                閉じる
              </button>
            </form>
            <div className="storedialog__body">
              {ranked.map((metric) => (
                <StoreLine key={metric.storeId} metric={metric} shareBase={shareBase} unit={unit} />
              ))}
            </div>
          </dialog>
        </>
      )}
    </div>
  );
}
